<template>
    <div class="quiz-container">
        <StepProgress
            :current-step="currentStep"
            :step-length="stepLength"
            final-step-label="yourSituation"
        />
        <form @submit.prevent="submitAnswer">
            <Question v-if="currentQuestion" v-model="selectedOption" :data="currentQuestion" />
            <p v-if="errorMsg" class="form-error question-error" aria-live="assertive">
                {{ errorMsg }}
            </p>
            <div v-if="currentResult" class="quiz-results">
                <Result v-if="currentResult" :data="currentResult" :answers="answers" />
            </div>
            <div class="quiz-controls">
                <button v-if="answers.length" type="button" class="button is-outlined" @click="goToPrevious">
                    {{ t('prev') }}
                </button>
                <button v-if="!currentResultKey" type="submit" class="button">{{ t('next') }}</button>
                <button v-if="showRestart" type="button" class="button" @click="restart">{{ t('newSearch') }}</button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';

import Question from '@/best-fare-students/components/Question';
import Result from '@/best-fare-students/components/Result';
import StepProgress from '@/shared/StepProgress';

const props = defineProps({
    dataTree: {
        type: Object,
        default: () => ({}),
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            prev: 'Précédent',
            next: 'Suivant',
            newSearch: 'Nouvelle recherche',
            errorMsg: 'Veuillez sélectionner une option pour continuer.',
            resultNotFound:
                "Ce cas de figure n'est pas encore configuré. Pour nous joindre, rendez-vous sur <a href='https://www.rtcquebec.ca/service-la-clientele/pour-nous-joindre'>notre site</a> pour plus d'information",
        },
        en: {
            prev: 'Previous',
            next: 'Next',
            newSearch: 'New search',
            errorMsg: 'Please select an option to continue.',
            resultNotFound:
                "This scenario is not yet configured. To contact us, go to<a href='https://www.rtcquebec.ca/en/customer-service/contact-us'>our website</a> for more information",
        },
    },
});

const currentStep = ref(0);
const stepLength = ref(4);
const currentQuestionKey = ref('start');
const answers = ref([]);
const currentResultKey = ref();
const selectedOption = ref();
const errorMsg = ref();

const questions = computed(() => props.dataTree.questions);
const currentQuestion = computed(() => {
    return questions.value[currentQuestionKey.value];
});
const showRestart = computed(() => !currentQuestion.value || !currentQuestion.value.options);
const currentResult = computed(() => {
    if (currentResultKey.value) {
        const result = props.dataTree.results.find(
            (result) => result.resultKeys && result.resultKeys.includes(currentResultKey.value),
        );
        return result ?? { situation: [t('resultNotFound')] };
    }
    return null;
});

function submitAnswer() {
    if (!selectedOption.value) {
        errorMsg.value = t('errorMsg');
        return;
    }
    answers.value.push({
        questionKey: currentQuestionKey.value,
        label: currentQuestion.value.summaryLabel,
        value: selectedOption.value.label,
        key: selectedOption.value.key,
        next: selectedOption.value.next,
    });
    errorMsg.value = '';
    if (currentQuestion.value.options && selectedOption.value.next) {
        currentQuestionKey.value = selectedOption.value.next;
        goNext();
    }
}

function goNext() {
    if (!selectedOption.value) return;
    goTo(selectedOption.value.next);
    selectedOption.value = null;
}

function goTo(next) {
    if (next.type === 'questions') {
        currentQuestionKey.value = next.key;
        currentResultKey.value = null;
        if (answers.value.length >= 3) {
            stepLength.value++;
        }
        currentStep.value += 1;
    } else if (next.type === 'results') {
        currentResultKey.value = getResultKey();
        currentQuestionKey.value = null;
        stepLength.value = answers.value.length >= 4 ? answers.value.length + 1 : 4;
        currentStep.value = stepLength.value - 1;
    }
}

function goToPrevious() {
    if (answers.value.length > 0) {
        const prevAnswer = answers.value.pop();
        currentQuestionKey.value = prevAnswer.questionKey;
        selectedOption.value = {
            label: prevAnswer.value,
            key: prevAnswer.key,
            next: prevAnswer.next,
        };
        errorMsg.value = '';
        currentResultKey.value = null;
        currentStep.value -= 1;
    }
}

function getResultKey() {
    return answers.value.map((answer) => answer.key).join('-');
}

function restart() {
    currentQuestionKey.value = 'start';
    currentResultKey.value = null;
    answers.value = [];
    selectedOption.value = null;
    errorMsg.value = '';
    currentStep.value = 0;
    stepLength.value = 4;
}
</script>

<style lang="scss" scoped>
.quiz-container {
    padding: 24px 16px 32px;
    background-color: $color-white;
    border-radius: 20px;
    @include breakpoint(medium) {
        padding: 40px;
    }
}
.question-error {
    margin-top: 0;
}
.quiz-results {
    margin-bottom: 40px;
}
.quiz-controls {
    display: flex;
    gap: 8px;
    .button {
        @media (max-width: 400px) {
            padding: 12px 14px;
        }
    }
}
</style>
